/**
 * Helpers for the clip search box in SharedVideoPlayer.
 *
 * Clips are addressed by their index into `tagList` everywhere in the player
 * (selection, current clip, seeking), so filtering must never renumber them.
 * These helpers work in original indices for that reason.
 */

/**
 * Case-insensitive match of a clip's search text against a query.
 * Every whitespace-separated token must appear, so "iosif goal" matches
 * "Alexandros Iosif - Shot - Penalty - Goal".
 *
 * @param {string} searchText - Text built from the clip's visible labels
 * @param {string} query - Raw contents of the search box
 * @returns {boolean} True when the clip should stay visible
 */
export const clipMatchesQuery = (searchText, query) => {
    const tokens = (query || '').toLowerCase().trim().split(/\s+/).filter(Boolean);

    if (tokens.length === 0) return true;

    const haystack = (searchText || '').toLowerCase();

    return tokens.every((token) => haystack.includes(token));
};

/**
 * Indices of the clips that match the query, in original tagList order.
 *
 * @param {Array} tagList - Full clip list
 * @param {string} query - Raw contents of the search box
 * @param {function} toSearchText - Builds the searchable text for one clip
 * @returns {Array<number>} Ascending original indices
 */
export const filterClipIndices = (tagList, query, toSearchText) => {
    if (!tagList || tagList.length === 0) return [];

    return tagList.reduce((acc, item, index) => {
        if (clipMatchesQuery(toSearchText(item), query)) acc.push(index);

        return acc;
    }, []);
};

/**
 * Next clip to play when stepping through the visible set.
 *
 * Works whether or not currentIndex is itself visible: stepping forward lands
 * on the first visible clip after it, backward on the last visible clip before
 * it. That keeps the skip buttons usable after the playing clip is filtered out.
 *
 * @param {Array<number>} visibleIndices - Ascending original indices
 * @param {number} currentIndex - Original index of the current clip
 * @param {number} step - Positive to go forward, negative to go back
 * @returns {number|null} Original index to play, or null at the ends
 */
export const nextVisibleIndex = (visibleIndices, currentIndex, step) => {
    if (!visibleIndices || visibleIndices.length === 0) return null;

    if (step > 0) {
        const next = visibleIndices.find((index) => index > currentIndex);

        return next === undefined ? null : next;
    }

    const previous = visibleIndices.filter((index) => index < currentIndex);

    return previous.length === 0 ? null : previous[previous.length - 1];
};
